import React, { useState, FunctionComponent, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { IAppState } from '../../../../common/models/store/IAppState';
import { connect } from 'react-redux';
import styles from './styles.module.scss';
import { recordFilterTypes, RecordFilterType } from '../../../../common/enums/RecordFilterType';
import { map, filter as rfilter, append, equals } from 'ramda';
import { arrayEntriesEqual } from '../../../../common/helpers/arrayHelper';
import Checkbox from '../../../../components/Checkbox';
import { User } from '../../../../common/models/user/User';
import { IGlobalSearchFilter } from '../../../../common/models/filter/IGlobalSearchFilter';
import AssignmentFilterBody from '../../../../containers/AssignmentFilterBody';
import Icon from '../../../../components/Icon';
import LinkButton from '../../../../components/LinkButton';
import { IAssignmentFilter } from '../../../../common/models/filter/IAssignmentFilter';

interface IGlobalSearchFiltersProps {
  user?: User;
  filter: IGlobalSearchFilter;
  defaultFilter: IGlobalSearchFilter;
  hidePopover?: IBindingAction;
  onUpdateFilters: IBindingCallback1<Partial<IGlobalSearchFilter>>;
}

const GlobalSearchFilters: FunctionComponent<IGlobalSearchFiltersProps> = ({
  defaultFilter,
  filter,
  onUpdateFilters,
  hidePopover
}) => {
  let select = filter.recordType ? filter.recordType : defaultFilter.recordType;
  if (select[0] === RecordFilterType.All) {
    select = recordFilterTypes;
  }

  const [selected, setSelected] = useState(select);
  const [assigmentFilterRadio, setAssigmentFilterRadio] = useState(filter.assigmentType);

  useEffect(() => {
    if (arrayEntriesEqual(selected, recordFilterTypes)) {
      onUpdateFilters({
        recordType: [RecordFilterType.All]
      });
      return;
    }
    onUpdateFilters({
      recordType: selected
    });
  }, [selected]);

  const onSelect = (t: RecordFilterType) => {
    if (selected.includes(t)) {
      const newSelected = rfilter(s => t !== s, selected);
      setSelected(newSelected.length ? newSelected : recordFilterTypes);
      return;
    }
    const newSelected = append(t, selected);
    setSelected(newSelected);
  };

  const TypeFilter = () => (
    <div className="d-flex flex-column">
      {map(t => (
        <div key={t} className={styles.typeFilter}>
          <Checkbox
            checked={selected.includes(t)}
            label={{ id: t, defaultMessage: t }}
            onChange={() => onSelect(t)}
          />
        </div>
      ), recordFilterTypes)}
    </div>
  );

  const getCheckedNodes = () => {
    const result = [];
    if (filter.userIds) {
      result.push(...filter.userIds);
    }
    if (filter.teamIds) {
      result.push(...filter.teamIds);
    }
    return result;
  };

  const onUpdateAssigmentFilter = (updatedFilter: Partial<IAssignmentFilter>) => {
    if (updatedFilter.assigmentType) {
      setAssigmentFilterRadio(updatedFilter.assigmentType);
    }
    onUpdateFilters(updatedFilter);
  };

  const isFilterDefault = () => equals({ ...defaultFilter, input: filter.input }, filter);

  return (
    <div className={styles.container}>
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <span className={`avenir-font ${styles.headerLabel}`}>
            <FormattedMessage id="search.filters" defaultMessage="Filters" />
          </span>
          {
            !isFilterDefault() && (
              <div className="ml-2">
                <LinkButton
                  textIntlId="searchResult.resetFilters"
                  defaultMessage="reset filters"
                  onClick={() => {
                    onUpdateFilters({ ...defaultFilter, input: filter.input });
                    setAssigmentFilterRadio(defaultFilter.assigmentType);
                    setSelected(recordFilterTypes);
                  }}
                />
              </div>
            )
          }
        </div>
        <div
          role="presentation"
          onClick={hidePopover}
          className="cursor-pointer"
        >
          <Icon icon="close" />
        </div>
      </div>
      <div>
        <div className={`t-14 ${styles.label}`}>
          <FormattedMessage id="search.filterByType" defaultMessage="Filter by type" />
        </div>
        <TypeFilter />
      </div>
      <div>
        <div className={`t-14 ${styles.label}`}>
          <FormattedMessage id="search.filterByAssignedUser" defaultMessage="Filter by assigned user" />
        </div>
        <AssignmentFilterBody
          onChange={onUpdateAssigmentFilter}
          defaultCheckboxChecked={getCheckedNodes()}
          defaultRadioChecked={filter.assigmentType}
          radioChecked={assigmentFilterRadio}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ globalSearch: { recentIds } }: IAppState) => ({ recentIds });

export default connect(mapStateToProps)(GlobalSearchFilters);
